import { memo, MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Box, IconButton, ListItemButton, Menu, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { Nullable, PartialBy } from '@global/types';
import { IActionMetadata, IPolicyAction, IUiAction } from '@tymely/atoms';
import { MenuList, Option } from '@tymely/components';
import { useActionMetadataExecutorSchemaQuery, useActionsMetadataMapping } from '@tymely/services';
import { newUiId, schemaEmpty } from '@tymely/utils';
import { js_beautify } from 'js-beautify';
import jsonSchemaDerefLocal from 'json-schema-deref-local';

type OptionalIdPolicyAction = PartialBy<IUiAction, 'uiId'>;

const AddActionButton = memo(
    (props: {
        addAction: (actionMetadataId: IUiAction['action_metadata_id'], args: object) => void;
        actionsMetadata: IActionMetadata[];
        disabled: boolean;
    }) => {
        const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
        const open = Boolean(anchorEl);

        const closeMenu = useCallback(() => {
            setAnchorEl(null);
        }, []);

        const handleClick = useCallback(
            (event: MouseEvent<HTMLElement>) => {
                if (!props.disabled) {
                    setAnchorEl(event.currentTarget);
                }
            },
            [props.disabled],
        );

        const [selectedId, setSelectedId] = useState<IUiAction['action_metadata_id']>();

        const addAction = useCallback(
            (opt: Nullable<Option<number>[]>) => {
                if (!opt) return;
                setSelectedId(opt[0].value);
            },
            [closeMenu, props.addAction],
        );

        const { data: executorSchema } = useActionMetadataExecutorSchemaQuery(selectedId);
        useEffect(() => {
            if (!selectedId || executorSchema === undefined) return;
            try {
                const argsJson = js_beautify(
                    JSON.stringify(
                        schemaEmpty(jsonSchemaDerefLocal(executorSchema) as Parameters<typeof schemaEmpty>[0]),
                    ),
                );
                const args = JSON.parse(argsJson) as IUiAction['args'];
                props.addAction(selectedId, args);
                setSelectedId(undefined);
                closeMenu();
            } catch (e) {
                console.error(e);
                props.addAction(selectedId, {});
            }
        }, [selectedId, executorSchema]);

        return (
            <>
                <IconButton title="New Action" onClick={handleClick} disabled={props.disabled}>
                    <AddIcon />
                </IconButton>
                <Menu
                    aria-labelledby="add-action-button"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={closeMenu}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    slotProps={{ paper: { sx: { minWidth: 360, ul: { pt: 0 } } } }}
                    sx={{ maxHeight: 500 }}
                >
                    <MenuList
                        disabled={!!selectedId}
                        options={props.actionsMetadata.map((md) => ({ name: md.title, value: md.id }))}
                        open={open}
                        multiple={false}
                        searchable
                        selectedOptions={[]}
                        onSelect={addAction}
                    />
                </Menu>
            </>
        );
    },
);

AddActionButton.displayName = 'AddActionButton';

type ActionMenuProps<A extends OptionalIdPolicyAction> = {
    idKey: 'id' | 'uiId';
    actions: PartialBy<A, 'action_metadata'>[];
    setActions: (actions: PartialBy<A, 'action_metadata'>[]) => void;
    onSelectAction?: (action: A) => void;
    selectedActionId?: number | string;
    disabled?: boolean;
    size?: 'small' | 'medium';
};

const ActionMenu = <A extends OptionalIdPolicyAction>(props: ActionMenuProps<A>) => {
    const { actionsMetadata, isSuccess, isLoading, isError } = useActionsMetadataMapping();

    const actionsWithMetadata: A[] = useMemo(
        () =>
            props.actions
                .map((action) =>
                    action.action_metadata
                        ? (action as unknown as A)
                        : ({
                              ...action,
                              action_metadata: actionsMetadata[action.action_metadata_id],
                          } as unknown as A),
                )
                .sort((a1, a2) => a1.order - a2.order),
        [props.actions, actionsMetadata],
    );

    const setActions = (actions: typeof props.actions) => {
        props.setActions(actions.map((action, order) => ({ ...action, order })));
    };

    const moveActionUp = (idx: number) => {
        if (idx === 0) {
            return;
        }

        setActions(
            props.actions
                .slice(0, idx - 1)
                .concat(props.actions[idx], props.actions[idx - 1], ...props.actions.slice(idx + 1))
                .map((action, order) => ({ ...action, order })),
        );
    };

    const moveActionDown = (idx: number) => {
        if (idx === props.actions.length - 1) {
            return;
        }

        setActions(
            props.actions
                .slice(0, idx)
                .concat(props.actions[idx + 1], props.actions[idx], ...props.actions.slice(idx + 2))
                .map((action, order) => ({ ...action, order })),
        );
    };

    const addAction = (actionMetadataId: IUiAction['action_metadata_id'], args: object) => {
        const newAction = {
            [props.idKey]: props.idKey === 'id' ? Math.floor(Math.random() * 0x7fffffff) : newUiId(),
            action_metadata_id: actionMetadataId,
            order: props.actions.length,
            args,
        } as unknown as (typeof props.actions)[number];

        setActions([...props.actions, newAction].map((action, order) => ({ ...action, order })));
    };

    const removeAction = (idx: number) => {
        setActions(props.actions.filter((_, pos) => pos !== idx).map((action, order) => ({ ...action, order })));
    };

    return (
        <Box flex={1}>
            {isError && 'Error'}
            {isLoading && 'Loading...'}
            {isSuccess &&
                actionsWithMetadata.map((action, idx) => (
                    <Box key={idx} display="flex" gap={1}>
                        <IconButton
                            disabled={props.disabled}
                            onClickCapture={() => removeAction(idx)}
                            sx={{ p: 0 }}
                            size="small"
                        >
                            <DeleteIcon />
                        </IconButton>
                        <IconButton
                            disabled={props.disabled || idx === 0}
                            onClickCapture={() => moveActionUp(idx)}
                            sx={{ p: 0 }}
                            size="small"
                        >
                            <ArrowUpwardIcon />
                        </IconButton>
                        <IconButton
                            disabled={props.disabled || idx === actionsWithMetadata.length - 1}
                            onClickCapture={() => moveActionDown(idx)}
                            sx={{ p: 0 }}
                            size="small"
                        >
                            <ArrowDownwardIcon />
                        </IconButton>
                        <ListItemButton
                            selected={action[props.idKey] === props.selectedActionId}
                            disabled={props.disabled}
                            onClick={() => props.onSelectAction?.(action)}
                            sx={{ px: 0, py: 0.5 }}
                        >
                            <Typography noWrap overflow="hidden" textOverflow="ellipsis" sx={{ flexGrow: '1', ml: 1 }}>
                                {action.action_metadata.title}
                            </Typography>
                        </ListItemButton>
                    </Box>
                ))}
            <Box>
                <AddActionButton
                    addAction={addAction}
                    actionsMetadata={Object.values(actionsMetadata)}
                    disabled={!isSuccess || Boolean(props.disabled)}
                />
            </Box>
        </Box>
    );
};

export const ActionMenuById = memo((props: Omit<ActionMenuProps<IPolicyAction>, 'idKey'>) =>
    ActionMenu<IPolicyAction>({ ...props, idKey: 'id' }),
);

ActionMenuById.displayName = 'ActionMenuById';
